import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Table, Button, Form, Alert, Spinner, Modal } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { AsyncPaginate } from "react-select-async-paginate";
import { authFetch } from "../utils/authFetch";
import { toast } from "react-toastify";

const num = (v) => {
  const x = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(x) ? x : 0;
};

export function RecipeForm({ initialDoc, onSubmit, busy, onRefresh }) {
  const [err, setErr] = useState("");
  const isEdit = !!initialDoc?.id;

  // Header state
  const [selProdusFinit, setSelProdusFinit] = useState(null);
  const [codReteta, setCodReteta] = useState("");
  const [denumire, setDenumire] = useState("");
  const [dataStart, setDataStart] = useState(new Date().toISOString().slice(0, 10));
  const [dataStop, setDataStop] = useState("");
  const [activa, setActiva] = useState(true);
  const [observatii, setObservatii] = useState("");

  // Client-side lines for Creation mode
  const [localLines, setLocalLines] = useState([]);

  // Ingredient Modal state
  const [showLineModal, setShowLineModal] = useState(false);
  const [currentLine, setCurrentLine] = useState(null);
  const [lineForm, setLineForm] = useState({
    material_id: "",
    cantitate: 0,
    um_id: "",
    pierdere_proc: 0,
  });
  const [optMaterial, setOptMaterial] = useState(null);
  const [optUm, setOptUm] = useState(null);

  const fetchPartById = async (id) => {
    if (!id) return null;
    try {
      const data = await authFetch(`/bxpart/${id}`);
      return { value: id, label: `${data.cod || ""} — ${data.denumire}`.trim() };
    } catch (e) {
      return { value: id, label: `Articol #${id}` };
    }
  };

  const fetchUmById = async (id) => {
    if (!id) return null;
    try {
      const data = await authFetch(`/bxums/${id}`);
      return { value: id, label: data.descriere || `UM #${id}` };
    } catch (e) {
      return { value: id, label: `UM #${id}` };
    }
  };

  // Populate form fields on edit mode
  useEffect(() => {
    if (initialDoc) {
      setCodReteta(initialDoc.cod_reteta || "");
      setDenumire(initialDoc.denumire || "");
      setDataStart(initialDoc.data_start ? initialDoc.data_start.slice(0, 10) : new Date().toISOString().slice(0, 10));
      setDataStop(initialDoc.data_stop ? initialDoc.data_stop.slice(0, 10) : "");
      setActiva(initialDoc.activa === 1);
      setObservatii(initialDoc.observatii || "");

      if (initialDoc.produs_finit_id && !selProdusFinit) {
        fetchPartById(initialDoc.produs_finit_id).then((opt) => opt && setSelProdusFinit(opt));
      }
    }
  }, [initialDoc]);

  // Load active recipe lines when selecting a finished product (in creation mode)
  useEffect(() => {
    if (!isEdit && selProdusFinit?.value) {
      const finitId = selProdusFinit.value;
      const loadActiveRecipeLines = async () => {
        try {
          const data = await authFetch(`/bxreteta/activa/${finitId}`);
          if (data && data.linii && data.linii.length > 0) {
            const mapped = data.linii.map((l) => ({
              material_id: l.material_id,
              cantitate: l.cantitate,
              um_id: l.um_id,
              pierdere_proc: l.pierdere_proc || 0,
              material_label: `${l.material_cod_str || ""} — ${l.material_denumire || ""}`.trim(),
              material_cod_str: l.material_cod_str || "",
              material_denumire: l.material_denumire || "",
              um_denumire: l.um_denumire || `UM #${l.um_id}`,
            }));
            setLocalLines(mapped);
            toast.info(`S-au copiat ${mapped.length} ingrediente din rețeta activă anterioară.`);
          } else {
            setLocalLines([]);
          }
        } catch (e) {
          // No active recipe or fetch failed, keep lines empty
          setLocalLines([]);
        }
      };
      loadActiveRecipeLines();
    }
  }, [selProdusFinit, isEdit]);

  // Load select options
  const loadParts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const data = await authFetch(`/bxpart/?skip=0&limit=20&q=${encodeURIComponent(q)}`);
      const items = data?.items || data || [];
      return items.map((p) => ({
        value: p.id,
        label: `${p.cod || ""} — ${p.denumire}`.trim(),
        __raw: p,
      }));
    } catch (e) {
      return [];
    }
  };

  const loadPartOptions = async (search, prevOptions, additional) => {
    const skip = prevOptions.length;
    try {
      const q = (search || "").trim();
      const data = await authFetch(`/bxpart/?skip=${skip}&limit=20&q=${encodeURIComponent(q)}`);
      const items = data?.items || data || [];
      const options = items.map((p) => ({
        value: p.id,
        label: `${p.cod || ""} — ${p.denumire}`.trim(),
        __raw: p,
      }));
      return {
        options,
        hasMore: items.length === 20,
        additional: {},
      };
    } catch (e) {
      return { options: [], hasMore: false };
    }
  };

  const loadUmOptions = async (search, prevOptions, additional) => {
    const skip = prevOptions.length;
    try {
      const q = (search || "").trim();
      const data = await authFetch(`/bxums/?skip=${skip}&limit=20&search=${encodeURIComponent(q)}`);
      const items = data?.items || data || [];
      const options = items.map((u) => ({
        value: u.id,
        label: u.descriere || u.denumire || `UM #${u.id}`,
      }));
      return {
        options,
        hasMore: items.length === 20,
        additional: {},
      };
    } catch (e) {
      return { options: [], hasMore: false };
    }
  };

  // Modal Line handlers
  const handleOpenAddLine = () => {
    setCurrentLine(null);
    setLineForm({ material_id: "", cantitate: 0, um_id: "", pierdere_proc: 0 });
    setOptMaterial(null);
    setOptUm(null);
    setShowLineModal(true);
  };

  const handleOpenEditLine = (item) => {
    setCurrentLine(item);
    setLineForm({
      material_id: item.material_id,
      cantitate: item.cantitate,
      um_id: item.um_id,
      pierdere_proc: item.pierdere_proc || 0,
    });
    setOptMaterial({
      value: item.material_id,
      label: `${item.material_cod_str || ""} — ${item.material_denumire || ""}`.trim() || `Material #${item.material_id}`,
    });
    setOptUm({
      value: item.um_id,
      label: item.um_denumire || `UM #${item.um_id}`,
    });
    setShowLineModal(true);
  };

  const handleSaveLine = async (e) => {
    e.preventDefault();
    if (!lineForm.material_id || !lineForm.um_id) {
      toast.warning("Alege materialul și U.M.");
      return;
    }

    const payload = {
      material_id: Number(lineForm.material_id),
      cantitate: num(lineForm.cantitate),
      um_id: Number(lineForm.um_id),
      pierdere_proc: num(lineForm.pierdere_proc),
    };

    if (isEdit) {
      // Direct API update in Edit mode
      try {
        if (currentLine) {
          await authFetch(`/bxreteta/linii/${currentLine.id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
          });
          toast.success("Ingredient actualizat.");
        } else {
          await authFetch(`/bxreteta/${initialDoc.id}/linii`, {
            method: "POST",
            body: JSON.stringify(payload),
          });
          toast.success("Ingredient adăugat.");
        }
        setShowLineModal(false);
        if (onRefresh) onRefresh();
      } catch (err) {
        toast.error("Eroare la salvare ingredient: " + err.message);
      }
    } else {
      // Local state update in Creation mode
      if (currentLine) {
        setLocalLines((prev) =>
          prev.map((l) => (l.material_id === currentLine.material_id ? { ...l, ...payload, material_label: optMaterial.label, um_denumire: optUm.label } : l))
        );
      } else {
        setLocalLines((prev) => [
          ...prev,
          {
            ...payload,
            material_label: optMaterial.label,
            material_cod_str: optMaterial.__raw?.cod || "",
            material_denumire: optMaterial.__raw?.denumire || "",
            um_denumire: optUm.label,
          },
        ]);
      }
      setShowLineModal(false);
    }
  };

  const handleDeleteLine = async (item) => {
    if (!window.confirm(`Ștergi materialul ${item.material_denumire || ""} din rețetă?`)) return;

    if (isEdit) {
      try {
        await authFetch(`/bxreteta/linii/${item.id}`, { method: "DELETE" });
        toast.success("Ingredient șters.");
        if (onRefresh) onRefresh();
      } catch (err) {
        toast.error("Eroare la ștergere: " + err.message);
      }
    } else {
      setLocalLines((prev) => prev.filter((l) => l.material_id !== item.material_id));
    }
  };

  const handleSaveRecipe = (e) => {
    e.preventDefault();
    if (!selProdusFinit?.value) {
      setErr("Alege produsul finit.");
      return;
    }
    if (!codReteta.trim()) {
      setErr("Introduceți codul rețetei.");
      return;
    }

    const payload = {
      produs_finit_id: selProdusFinit.value,
      cod_reteta: codReteta.trim(),
      denumire: denumire.trim(),
      data_start: dataStart ? dataStart + "T00:00:00" : null,
      data_stop: dataStop ? dataStop + "T00:00:00" : null,
      activa: activa ? 1 : 0,
      observatii: observatii.trim() || null,
      linii: isEdit ? [] : localLines.map(l => ({
        material_id: l.material_id,
        cantitate: l.cantitate,
        um_id: l.um_id,
        pierdere_proc: l.pierdere_proc
      })),
    };

    onSubmit(payload);
  };

  const displayLines = isEdit ? initialDoc.linii || [] : localLines;

  return (
    <>
      <Form onSubmit={handleSaveRecipe}>
      {err && <Alert variant="danger">{err}</Alert>}

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Produs Finit (Destinație Rețetă)</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={selProdusFinit}
              loadOptions={loadParts}
              onChange={setSelProdusFinit}
              placeholder="Caută după denumire/cod..."
              isDisabled={isEdit}
              required
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Cod Rețetă</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex: R1"
              value={codReteta}
              onChange={(e) => setCodReteta(e.target.value)}
              required
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Denumire Rețetă</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex: Reteta Standard"
              value={denumire}
              onChange={(e) => setDenumire(e.target.value)}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Dată Start Valabilitate</Form.Label>
            <Form.Control
              type="date"
              value={dataStart}
              onChange={(e) => setDataStart(e.target.value)}
              required
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Dată Stop Valabilitate (Opțional)</Form.Label>
            <Form.Control
              type="date"
              value={dataStop}
              onChange={(e) => setDataStop(e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={6} className="d-flex align-items-center">
          <Form.Group className="mb-0 mt-4">
            <Form.Check
              type="checkbox"
              label="Rețetă Activă (va dezactiva rețetele vechi ale acestui produs)"
              checked={activa}
              onChange={(e) => setActiva(e.target.checked)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Observații</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={observatii}
              onChange={(e) => setObservatii(e.target.value)}
              placeholder="Informații suplimentare..."
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <h5 className="m-0 text-secondary">Ingrediente & Materii Prime (BOM)</h5>
        <Button variant="outline-success" size="sm" onClick={handleOpenAddLine} disabled={busy}>
          ➕ Adaugă Ingredient
        </Button>
      </div>

      <Table bordered size="sm" className="align-middle">
        <thead className="table-dark">
          <tr>
            <th>Cod Material</th>
            <th>Material / Componentă</th>
            <th>U.M.</th>
            <th style={{ width: 150 }} className="text-end">Cantitate</th>
            <th style={{ width: 150 }} className="text-end">Pierdere %</th>
            <th style={{ width: 120 }} className="text-center">Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {displayLines.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted py-3">
                Nu există ingrediente adăugate în această rețetă.
              </td>
            </tr>
          ) : null}

          {displayLines.map((ln, idx) => (
            <tr key={ln.id || idx}>
              <td>{ln.material_cod_str || "-"}</td>
              <td>{ln.material_label || ln.material_denumire || `Material #${ln.material_id}`}</td>
              <td>{ln.um_denumire || "-"}</td>
              <td className="text-end font-monospace">{Number(ln.cantitate || 0).toFixed(4)}</td>
              <td className="text-end font-monospace">{Number(ln.pierdere_proc || 0).toFixed(2)}%</td>
              <td className="text-center">
                <Button variant="outline-warning" size="sm" className="me-2 py-0 px-2" onClick={() => handleOpenEditLine(ln)}>
                  ✏️
                </Button>
                <Button variant="outline-danger" size="sm" className="py-0 px-2" onClick={() => handleDeleteLine(ln)}>
                  🗑️
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="primary" type="submit" disabled={busy}>
          {busy ? <Spinner animation="border" size="sm" /> : "Salvează Rețetă"}
        </Button>
      </div>
    </Form>

    {showLineModal && (
      <Modal show={showLineModal} onHide={() => setShowLineModal(false)} size="lg">
        <Form onSubmit={handleSaveLine}>
          <Modal.Header closeButton>
            <Modal.Title>{currentLine ? "Editează Ingredient" : "Adaugă Ingredient"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Material / Componentă</Form.Label>
                  <AsyncPaginate
                    value={optMaterial}
                    loadOptions={loadPartOptions}
                    defaultOptions
                    onChange={(opt) => {
                      setOptMaterial(opt);
                      const raw = opt?.__raw;
                      const umId = raw?.um?.id;
                      setLineForm((f) => ({
                        ...f,
                        material_id: opt?.value ?? "",
                        um_id: umId ?? f.um_id,
                      }));
                      if (umId) {
                        setOptUm({
                          value: umId,
                          label: raw?.um?.descriere || raw?.um?.denumire || `UM #${umId}`,
                        });
                      }
                    }}
                    placeholder="Caută material..."
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unitate de Măsură</Form.Label>
                  <AsyncPaginate
                    value={optUm}
                    loadOptions={loadUmOptions}
                    defaultOptions
                    onChange={(opt) => {
                      setOptUm(opt);
                      setLineForm((f) => ({ ...f, um_id: opt?.value ?? "" }));
                    }}
                    placeholder="Selectează U.M..."
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Cantitate</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={lineForm.cantitate}
                    onChange={(e) => setLineForm((f) => ({ ...f, cantitate: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Pierdere %</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={lineForm.pierdere_proc}
                    onChange={(e) => setLineForm((f) => ({ ...f, pierdere_proc: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLineModal(false)}>
              Anulează
            </Button>
            <Button variant="primary" type="submit">
              Salvează
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    )}
  </>
  );
}
